import axios from 'axios'
import { API_ERROR_CODES, ApiErrorCode, ApiErrorResponse } from '../../../libs/apiErrors'
import {
  ALBUM_ILLUSTS_LIMIT,
  ALBUM_NAME_MAX_LENGTH,
  ALBUMS_PER_USER_LIMIT,
  SUSPENSION_LIFT_REQUEST_ADMIN_COMMENT_MAX_LENGTH,
  SUSPENSION_LIFT_REQUEST_USER_COMMENT_MAX_LENGTH
} from '../../../libs/constants'
import { JapaneseText, l } from './localization'

const errorMessages: Record<ApiErrorCode, JapaneseText> = {
  [API_ERROR_CODES.NotAuthenticated]: 'ログインが必要です',
  [API_ERROR_CODES.InvalidParameters]: '入力内容が不適切です',
  [API_ERROR_CODES.PermissionDenied]: 'この操作を行う権限がありません',
  [API_ERROR_CODES.InternalServerError]: 'サーバーエラーが発生しました',
  [API_ERROR_CODES.ResourceNotFound]: '対象が見つかりませんでした',
  [API_ERROR_CODES.AlbumIllustLimitExceeded]: 'アルバムに追加できるイラストは{limit}件までです',
  [API_ERROR_CODES.InvalidAlbumId]: 'アルバムIDが不適切です',
  [API_ERROR_CODES.InvalidIllustId]: 'イラストIDが不適切です',
  [API_ERROR_CODES.AlbumIllustAlreadyAdded]: 'このイラストは追加済みです',
  [API_ERROR_CODES.InvalidAlbumName]: 'アルバム名は{limit}文字以内で入力してください',
  [API_ERROR_CODES.InvalidAlbumVisibility]: '公開設定が不適切です',
  [API_ERROR_CODES.InvalidIllustOrder]: 'イラストの並び順が不適切です',
  [API_ERROR_CODES.InvalidAlbumOrder]: 'アルバムの並び順が不適切です',
  [API_ERROR_CODES.InvalidUserId]: 'ユーザーIDが不適切です',
  [API_ERROR_CODES.InvalidPage]: 'ページ番号が不適切です',
  [API_ERROR_CODES.AlbumLimitExceeded]: '作成できるアルバムは{limit}件までです',
  [API_ERROR_CODES.PasswordRequired]: '先にパスワードを設定してください',
  [API_ERROR_CODES.LoginAttemptsExceeded]: 'ログイン試行回数の上限に達したため、しばらくしてから再度お試しください',
  [API_ERROR_CODES.InvalidCredentials]: 'ユーザーIDまたはパスワードが間違っています',
  [API_ERROR_CODES.AccountDeleted]: 'このアカウントは退会済みです',
  [API_ERROR_CODES.MessageRejected]: 'このメッセージは投稿できません',
  [API_ERROR_CODES.ProfileRejected]: 'このプロフィールは設定できません',
  [API_ERROR_CODES.CommentTooLong]: 'コメントが長すぎます',
  [API_ERROR_CODES.CommentLimitExceeded]: '同じイラストに投稿できるコメントは5件までです',
  [API_ERROR_CODES.CommentRejected]: 'コメントの内容が不適切な可能性があります。表現を修正のうえ再度送信してください。',
  [API_ERROR_CODES.PinnedIllustLimitExceeded]: 'ピンできるのは5件までです',
  [API_ERROR_CODES.ImageRequired]: '画像ファイルがありません',
  [API_ERROR_CODES.InvalidImage]: '画像ファイルが不適切です',
  [API_ERROR_CODES.InvalidImageContent]: '画像の内容が不適切です',
  [API_ERROR_CODES.TitleRequired]: 'タイトルを入力してください',
  [API_ERROR_CODES.CommentNotFound]: 'コメントが見つかりません',
  [API_ERROR_CODES.IllustNotFound]: 'イラストが見つかりません',
  [API_ERROR_CODES.IllustUpdateFailed]: 'イラストを更新できませんでした',
  [API_ERROR_CODES.SignoutFailed]: 'ログアウトに失敗しました',
  [API_ERROR_CODES.TagRequired]: 'タグ名を入力してください',
  [API_ERROR_CODES.PopularTagsFetchFailed]: '人気タグを取得できませんでした',
  [API_ERROR_CODES.InvalidRequestId]: 'リクエストIDが不正です。',
  [API_ERROR_CODES.InvalidStatus]: 'ステータスが不正です。',
  [API_ERROR_CODES.InvalidAwarenessType]: '申告内容が不正です。',
  [API_ERROR_CODES.CommentRequired]: 'コメントを入力してください。',
  [API_ERROR_CODES.UserCommentTooLong]: 'コメントは{limit}文字以内で入力してください。',
  [API_ERROR_CODES.AdminCommentTooLong]: 'コメントは{limit}文字以内で入力してください。',
  [API_ERROR_CODES.SuspensionRequestNotFound]: '対象のリクエストが見つかりません。',
  [API_ERROR_CODES.SuspensionRequestAlreadyResumed]: 'このリクエストはすでに再開済みです。',
  [API_ERROR_CODES.SuspensionRequestNotApproved]: '承認済みリクエストのみ再開できます。',
  [API_ERROR_CODES.SuspensionResumeUnavailable]: '再開可能日時になるまで利用再開できません。',
  [API_ERROR_CODES.SuspensionRequestUnavailable]: '現在は新規リクエストを作成できません。',
  [API_ERROR_CODES.SuspendedUserOnly]: '利用停止中のユーザーのみコメントを作成できます。',
  [API_ERROR_CODES.OpenSuspensionRequestOnly]: '審査中のリクエストのみ判定できます。',
  [API_ERROR_CODES.UserNotFound]: 'ユーザーが見つかりません。'
}

interface LegacyApiErrorResponse {
  error?: string
}

export const getApiErrorMessage = (error: unknown, fallback: JapaneseText): string => {
  if (!axios.isAxiosError<ApiErrorResponse | LegacyApiErrorResponse>(error)) return l(fallback)
  const apiError = error.response?.data?.error
  if (!apiError) return l(fallback)
  if (apiError in errorMessages) {
    const code = apiError as ApiErrorCode
    const limits: Partial<Record<ApiErrorCode, number>> = {
      [API_ERROR_CODES.AlbumIllustLimitExceeded]: ALBUM_ILLUSTS_LIMIT,
      [API_ERROR_CODES.InvalidAlbumName]: ALBUM_NAME_MAX_LENGTH,
      [API_ERROR_CODES.AlbumLimitExceeded]: ALBUMS_PER_USER_LIMIT,
      [API_ERROR_CODES.UserCommentTooLong]: SUSPENSION_LIFT_REQUEST_USER_COMMENT_MAX_LENGTH,
      [API_ERROR_CODES.AdminCommentTooLong]: SUSPENSION_LIFT_REQUEST_ADMIN_COMMENT_MAX_LENGTH
    }
    const limit = limits[code]
    return l(errorMessages[code], limit === undefined ? {} : { limit })
  }
  return apiError
}
